import { FunctionComponent, useEffect, useState } from "react"
import ModalHeader from "../../../shared/modalHeader"
import { TrexNavigator } from "../../../../classes/navigator/navigator"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import ModalFooter from "../../../shared/modalFooter"
import WellNotesFormComp from "../wellNotesForm/component"
import { useMutation, useQuery } from "@apollo/client"
import { WELL_NOTES_FOR_MODALS } from "../../../../graphql/queries/wellNotes"
import useWellNotesForm, { WellNotesFormManager } from "../wellNotesForm/form"
import { WELL_NOTES_UPDATE } from "../../../../graphql/mutations/well_notes"
import styles from "./update.module.scss"
import { WELL_NOTES_FOR_MODALS_FRAG } from "../../../../graphql/fragments/wellnote"
import { getFragmentData } from "../../../../generated"
import {
    WELL_NOTES_ABS_ROUTE,
    WELL_NOTES_DELETE_CHILD_ROUTE,
} from "../../../.."
import dayjs from "../../../../util/dayjs"
import { EmptyState } from "../../../shared/graphQlResponse"
import { useViewerContext } from "../../../../context/ViewerContext"
import { WellNoteVisibility } from "../../../../generated/graphql"
import { canRoleManageInternalWellNotes } from "../../../../util/rolePermissions/canRole"
import { getZoneInfoFromEnum } from "../../../../graphql/enum/zoneInfo"

const WellNotesUpdateModal: FunctionComponent = () => {
    // hooks
    const navigate = useNavigate()
    const { key: locationKey, pathname } = useLocation()
    const { wellId, noteId } = useParams()
    if (!wellId || !noteId) {
        throw new Error("wellId or noteId not resolved from url.")
    }
    const viewer = useViewerContext()
    const canManageInternalVisibility = canRoleManageInternalWellNotes(
        viewer.getViewer().role
    )
    const zoneInfo = viewer.getViewer().identity.zoneInfo
    const tz = getZoneInfoFromEnum(zoneInfo)

    // state
    const [showEmptyState, setEmptyState] = useState(false)

    // get well note by id
    const {
        error: queryError,
        loading: queryLoading,
        data: queryData,
    } = useQuery(WELL_NOTES_FOR_MODALS, {
        variables: {
            input: { ids: [noteId] },
        },
        fetchPolicy: "network-only",
    })

    // mutations
    const [updateWellNote, { loading: updateLoading, error: updateError }] =
        useMutation(WELL_NOTES_UPDATE)

    // form state and handlers
    const {
        formState,
        setFormState,
        handleNoteChange,
        handleOccurredAtChange,
        handleOccurredUntilChange,
        handleVisibilityChange,
        handleNoteBlur,
        handleOccurredAtBlur,
        handleOccurredUntilBlur,
        handleOccurredUntilClear,
    } = useWellNotesForm({
        data: {
            note: "",
            occurredAt: "",
            occurredUntil: "",
            initialOccurredUntil: "",
            zoneInfo: zoneInfo,
            visibility: WellNoteVisibility.Shared,
            wellIDs: [wellId],
            noteID: noteId,
        },
        submitDisabled: true,
        validation: {
            note: "",
            occurredAt: "",
            occurredUntil: "",
        },
    })

    // submission handler
    const handleSubmitUpdateNote = async () => {
        const formManager = new WellNotesFormManager(formState.data)
        // if the form is not valid - set updated state and do not submit by returning
        // our class and form state prevent submit being called in a not valid state - this is a safety net
        if (!formManager.isFormValid) {
            setFormState({
                data: formManager.formData,
                submitDisabled: true,
                validation: {
                    note: formManager.noteValidationState,
                    occurredAt: formManager.occurredAtValidationState,
                    occurredUntil: formManager.occurredUntilValidationState,
                },
            })
            return
        }
        // if form is valid upate well note note using the mutation
        const updateVariables = formManager.getWellNotesUpdateInput()
        await updateWellNote({
            variables: { WellNotesUpdateInput: updateVariables },
        })
            .then(() => {
                navigate(`${WELL_NOTES_ABS_ROUTE(wellId)}`)
            })
            .catch((error) => {
                // mutation error will be passed to the form from the mutation variables assigned above. This error is passed to the form for error handling
                console.error("Error updating well note:", error)
            })
    }

    // set form data after it has loaded
    useEffect(() => {
        if (queryData && queryData.wellNotesByIDs.wellNotes.length > 0) {
            const wellNote = getFragmentData(
                WELL_NOTES_FOR_MODALS_FRAG,
                queryData.wellNotesByIDs.wellNotes[0]
            )
            setFormState({
                data: {
                    ...formState.data,
                    note: wellNote.note,
                    // convert unix milliseconds to date string in profile timezone for datetime-local input
                    occurredAt: dayjs(wellNote.occurredAt.unixMilliseconds)
                        .tz(tz)
                        .format("YYYY-MM-DDTHH:mm"),
                    occurredUntil: wellNote.occurredUntil
                        ? dayjs(wellNote.occurredUntil.unixMilliseconds)
                              .tz(tz)
                              .format("YYYY-MM-DDTHH:mm")
                        : "",
                    initialOccurredUntil: wellNote.occurredUntil
                        ? dayjs(wellNote.occurredUntil.unixMilliseconds)
                              .tz(tz)
                              .format("YYYY-MM-DDTHH:mm")
                        : "",
                    visibility: wellNote.visibility,
                },
                submitDisabled: true,
                validation: {
                    note: "",
                    occurredAt: "",
                    occurredUntil: "",
                },
            })
        } else if (
            queryData &&
            queryData.wellNotesByIDs.wellNotes.length === 0
        ) {
            // if no note is found, set the empty state
            setEmptyState(true)
        }
    }, [queryData])

    return (
        <div className={styles.container}>
            <ModalHeader
                title="Update Well Note"
                trexNavigator={
                    new TrexNavigator(
                        {
                            navigateTo: -1,
                            locationKey: locationKey,
                            pathName: pathname,
                        },
                        navigate
                    )
                }
            />
            {showEmptyState && (
                <EmptyState outSideDivStyle={styles.queryEmpty}>
                    <> No well note with this id was found.</>
                </EmptyState>
            )}
            {!showEmptyState && (
                <>
                    <WellNotesFormComp
                        handleNoteChange={handleNoteChange}
                        handleOccurredAtChange={handleOccurredAtChange}
                        handleOccurredUntilChange={handleOccurredUntilChange}
                        handleVisibilityChange={handleVisibilityChange}
                        handleOccurredAtBlur={handleOccurredAtBlur}
                        handleOccurredUntilBlur={handleOccurredUntilBlur}
                        handleOccurredUntilClear={handleOccurredUntilClear}
                        handleNoteBlur={handleNoteBlur}
                        formState={formState}
                        loading={updateLoading || queryLoading}
                        fetchError={queryError}
                        mutationError={updateError}
                        formType="update"
                        canManageInternalVisibility={
                            canManageInternalVisibility
                        }
                    />
                </>
            )}
            <ModalFooter
                advanceText="Update Note"
                previousText="Delete Well Note"
                previousButtonCondition="alert"
                handlePreviousClick={() => {
                    navigate(
                        `${WELL_NOTES_ABS_ROUTE(
                            wellId
                        )}/${WELL_NOTES_DELETE_CHILD_ROUTE(noteId)}`
                    )
                }}
                disablePrevious={
                    updateLoading ||
                    queryLoading ||
                    Boolean(updateError) ||
                    Boolean(queryError)
                }
                disableAdvance={
                    updateLoading ||
                    queryLoading ||
                    Boolean(updateError) ||
                    Boolean(queryError) ||
                    formState.submitDisabled
                }
                handleAdvanceClick={handleSubmitUpdateNote}
            />
        </div>
    )
}

export default WellNotesUpdateModal
